import { useEffect, useState } from 'react';

import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { logError } from '@iwLogger/IwDataDogLogger';

import { BankSelectionModal } from './BankSelectionModal';
import { getBanksFromContext } from './getBanksFromContext';
import { getLinkedAccounts } from './getLinkedAccounts';
import {
    ExtendIntentRecord,
    forgetExtendIntent,
    peekExtendIntent
} from './reconsent/extendIntent';
import {
    getConnectionsNeedingRenewal,
    getReconsentConnections
} from './reconsent/getReconsentConnections';
import { InitialRenewal } from './useBankSelectionFlow';
import { useGetOnlineAccounts } from './useGetOnlineAccounts';
import {
    clearTrueLayerParams,
    isTrueLayerFailureReturn,
    isTrueLayerSuccessReturn
} from '../../../iwRouter/LocationHelper';

export const OpenBankingSuccessRedirect = () => {
    const isSuccessReturn = isTrueLayerSuccessReturn();
    const [extendIntent] = useState(() =>
        isSuccessReturn ? peekExtendIntent() : null
    );
    const isTrueLayerReturn = isSuccessReturn || isTrueLayerFailureReturn();

    useEffect(() => {
        if (isTrueLayerReturn) forgetExtendIntent();
    }, [isTrueLayerReturn]);

    if (!isSuccessReturn) {
        return null;
    }

    return <OpenBankingSuccessRedirectContent extendIntent={extendIntent} />;
};

const OpenBankingSuccessRedirectContent = ({
    extendIntent
}: {
    extendIntent: ExtendIntentRecord | null;
}) => {
    const extendedOnlineAccountId = extendIntent?.onlineAccountId ?? null;
    const [isOpen, setIsOpen] = useState(true);
    const { data: onlineAccountsResponse, isLoading } = useGetOnlineAccounts();
    const { data: context } = useGetContext();

    const banks = getBanksFromContext(context);
    const linkedAccounts = getLinkedAccounts(onlineAccountsResponse, banks);
    const connections = getReconsentConnections(onlineAccountsResponse, banks);

    if (extendedOnlineAccountId && isLoading) {
        return null;
    }

    const extendedConnection = connections.find(
        (connection) => connection.onlineAccountId === extendedOnlineAccountId
    );

    if (extendedOnlineAccountId && !extendedConnection) {
        logError(
            'Open banking reconsent: renewed connection not found on return',
            {
                onlineAccountId: extendedOnlineAccountId
            }
        );
    }

    // Each renewal needs its own trip to the bank, so confirming here while
    // another connection still needs renewing would strand the rest.
    const initialRenewal: InitialRenewal | undefined = extendedConnection
        ? {
              connection: extendedConnection,
              hasMoreToRenew:
                  getConnectionsNeedingRenewal(connections, [
                      extendedConnection.onlineAccountId
                  ]).length > 0
          }
        : undefined;

    return (
        <BankSelectionModal
            isOpen={isOpen}
            initialStep="success"
            initialRenewal={initialRenewal}
            linkedAccounts={linkedAccounts}
            trackingSource={extendIntent?.source}
            onClose={() => {
                clearTrueLayerParams();
                setIsOpen(false);
            }}
        />
    );
};
